import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import {
  Tags,
  Search,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  ChevronDown,
  Database,
  Settings,
  Layers,
  Package,
  Wrench,
  Calendar,
  CreditCard,
  Heart,
  Megaphone,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info
} from 'lucide-react';

// Listing type icons and colors
const LISTING_TYPE_CONFIG = {
  PRODUCT: { icon: Package, color: 'bg-blue-500', label: 'Product' },
  SERVICE: { icon: Wrench, color: 'bg-green-500', label: 'Service' },
  RENTAL: { icon: Tags, color: 'bg-purple-500', label: 'Rental' },
  CLASSIFIED: { icon: Megaphone, color: 'bg-orange-500', label: 'Classified' },
  EVENT: { icon: Calendar, color: 'bg-pink-500', label: 'Event' },
  SUBSCRIPTION: { icon: CreditCard, color: 'bg-indigo-500', label: 'Subscription' },
  VOLUNTEERING: { icon: Heart, color: 'bg-red-500', label: 'Volunteering' }
};

// Attribute type options
const ATTRIBUTE_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' },
  { value: 'boolean', label: 'Yes/No (Boolean)' },
  { value: 'color', label: 'Color' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'range', label: 'Range' }
];

const TaxonomyLibrary = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Standard taxonomy state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState(null);
  
  // Custom taxonomy state
  const [customTaxonomies, setCustomTaxonomies] = useState([]);
  const [selectedListingType, setSelectedListingType] = useState('');
  const [editingTaxonomy, setEditingTaxonomy] = useState(null);
  const [showAttributeDialog, setShowAttributeDialog] = useState(false);
  const [newAttribute, setNewAttribute] = useState({
    key: '',
    label: '',
    type: 'text',
    required: false,
    is_filter: true,
    options: [],
    description: '',
    placeholder: '',
    unit: ''
  });

  // Fetch stats and sync status
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, syncRes] = await Promise.all([
        fetch(`${API_URL}/api/taxonomy/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/taxonomy/sync/status`)
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (syncRes.ok) {
        const syncData = await syncRes.json();
        setSyncStatus(syncData);
      }
    } catch (error) {
      console.error('Error fetching taxonomy data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // Fetch custom taxonomies
  const fetchCustomTaxonomies = useCallback(async () => {
    try {
      const url = selectedListingType 
        ? `${API_URL}/api/taxonomy/custom?listing_type=${selectedListingType}`
        : `${API_URL}/api/taxonomy/custom`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCustomTaxonomies(data.taxonomies || []);
      }
    } catch (error) {
      console.error('Error fetching custom taxonomies:', error);
    }
  }, [API_URL, token, selectedListingType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'custom') {
      fetchCustomTaxonomies();
    }
  }, [activeTab, fetchCustomTaxonomies]);

  // Trigger Shopify sync
  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${API_URL}/api/taxonomy/sync/shopify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        toast({
          title: 'Sync Started',
          description: 'Shopify taxonomy sync has been initiated. This may take a few minutes.',
        });
        // Poll for status
        const pollInterval = setInterval(async () => {
          const statusRes = await fetch(`${API_URL}/api/taxonomy/sync/status`);
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            setSyncStatus(statusData);
            if (statusData.status !== 'in_progress') {
              clearInterval(pollInterval);
              setSyncing(false);
              fetchData();
              if (statusData.status === 'completed') {
                toast({
                  title: 'Sync Completed',
                  description: `Successfully synced ${statusData.total_categories || 0} categories.`,
                });
              } else if (statusData.status === 'failed') {
                toast({
                  title: 'Sync Failed',
                  description: statusData.errors?.[0] || 'Unknown error occurred',
                  variant: 'destructive'
                });
              }
            }
          }
        }, 3000);
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to start sync');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      setSyncing(false);
    }
  };

  // Search standard taxonomy
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const res = await fetch(`${API_URL}/api/taxonomy/reference/search?q=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Get taxonomy details
  const handleViewTaxonomy = async (taxonomyId) => {
    try {
      const res = await fetch(`${API_URL}/api/taxonomy/reference/${taxonomyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedTaxonomy(data);
      }
    } catch (error) {
      console.error('Error fetching taxonomy:', error);
    }
  };

  // Add attribute to custom taxonomy
  const handleAddAttribute = async () => {
    if (!editingTaxonomy) return;

    try {
      // Validate
      if (!newAttribute.key || !newAttribute.label || !newAttribute.type) {
        toast({
          title: 'Validation Error',
          description: 'Key, Label, and Type are required',
          variant: 'destructive'
        });
        return;
      }

      const res = await fetch(`${API_URL}/api/taxonomy/custom/${editingTaxonomy.id}/attributes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAttribute)
      });

      if (res.ok) {
        const updated = await res.json();
        setEditingTaxonomy(updated);
        setCustomTaxonomies(prev => 
          prev.map(t => t.id === updated.id ? updated : t)
        );
        setShowAttributeDialog(false);
        setNewAttribute({
          key: '',
          label: '',
          type: 'text',
          required: false,
          is_filter: true,
          options: [],
          description: '',
          placeholder: '',
          unit: ''
        });
        toast({
          title: 'Attribute Added',
          description: `Added "${newAttribute.label}" to ${editingTaxonomy.name}`,
        });
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to add attribute');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Delete attribute from custom taxonomy
  const handleDeleteAttribute = async (attributeKey) => {
    if (!editingTaxonomy) return;

    try {
      const res = await fetch(
        `${API_URL}/api/taxonomy/custom/${editingTaxonomy.id}/attributes/${attributeKey}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        const updated = await res.json();
        setEditingTaxonomy(updated);
        setCustomTaxonomies(prev => 
          prev.map(t => t.id === updated.id ? updated : t)
        );
        toast({
          title: 'Attribute Removed',
          description: 'Attribute has been removed successfully',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove attribute',
        variant: 'destructive'
      });
    }
  };

  // Render stats cards
  const renderStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Shopify Categories</p>
              <p className="text-2xl font-bold">{stats?.total_standard_categories || 0}</p>
            </div>
            <Database className="h-8 w-8 text-blue-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Custom Taxonomies</p>
              <p className="text-2xl font-bold">{stats?.total_custom_taxonomies || 0}</p>
            </div>
            <Layers className="h-8 w-8 text-green-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Categories Mapped</p>
              <p className="text-2xl font-bold">{stats?.categories_with_mapping || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Unmapped Categories</p>
              <p className="text-2xl font-bold">{stats?.categories_without_mapping || 0}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-yellow-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render sync status
  const renderSyncStatus = () => (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Shopify Taxonomy Sync</CardTitle>
            <CardDescription>
              Sync product taxonomy from Shopify's official GitHub repository
            </CardDescription>
          </div>
          <Button 
            onClick={handleSync} 
            disabled={syncing || syncStatus?.status === 'in_progress'}
          >
            {syncing || syncStatus?.status === 'in_progress' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Status:</span>
            {syncStatus?.status === 'completed' && (
              <Badge variant="success" className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
            {syncStatus?.status === 'in_progress' && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                In Progress
              </Badge>
            )}
            {syncStatus?.status === 'failed' && (
              <Badge variant="destructive">
                <XCircle className="h-3 w-3 mr-1" />
                Failed
              </Badge>
            )}
            {syncStatus?.status === 'never_synced' && (
              <Badge variant="outline">Never Synced</Badge>
            )}
          </div>
          
          {syncStatus?.last_sync && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Last Sync:</span>
              <span>{new Date(syncStatus.last_sync).toLocaleString()}</span>
            </div>
          )}
          
          {syncStatus?.total_categories > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Categories:</span>
              <span>{syncStatus.total_categories}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Render standard taxonomy search
  const renderStandardTaxonomy = () => (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search Shopify taxonomy (e.g., 'shirts', 'electronics', 'furniture')..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handleSearch(e.target.value);
            }}
          />
        </div>
      </div>

      {searching && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!searching && searchResults.length > 0 && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category Name</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Attributes</TableHead>
                <TableHead>Match Score</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchResults.map((result) => (
                <TableRow key={result.id}>
                  <TableCell className="font-medium">{result.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">Level {result.level}</Badge>
                  </TableCell>
                  <TableCell>{result.attributes_count}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={result.score > 0.7 ? 'default' : result.score > 0.4 ? 'secondary' : 'outline'}
                    >
                      {Math.round(result.score * 100)}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewTaxonomy(result.id)}
                    >
                      View
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Info className="h-8 w-8 mx-auto mb-2" />
          <p>No categories found matching "{searchQuery}"</p>
        </Card>
      )}

      {/* Taxonomy details dialog */}
      <Dialog open={!!selectedTaxonomy} onOpenChange={() => setSelectedTaxonomy(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTaxonomy?.name}</DialogTitle>
            <DialogDescription>
              Shopify GID: {selectedTaxonomy?.shopify_node_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Badge variant="outline">Level {selectedTaxonomy?.level}</Badge>
              {selectedTaxonomy?.is_leaf && <Badge variant="secondary">Leaf Node</Badge>}
            </div>

            {selectedTaxonomy?.keywords?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Keywords</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedTaxonomy.keywords.map((kw, i) => (
                    <Badge key={kw.id || kw.name || `${kw.title || "kw"}-${i}`} variant="outline" className="text-xs">{kw}</Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedTaxonomy?.attributes?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Attributes ({selectedTaxonomy.attributes.length})</Label>
                <div className="mt-2 border rounded-md divide-y max-h-64 overflow-y-auto">
                  {selectedTaxonomy.attributes.map((attr, i) => (
                    <div key={attr.id || attr.name || `${attr.title || "attr"}-${i}`} className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{attr.label}</span>
                        <Badge variant="outline">{attr.type}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Key: {attr.key}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render custom taxonomies
  const renderCustomTaxonomies = () => (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <Select value={selectedListingType || "all"} onValueChange={(v) => setSelectedListingType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Listing Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Listing Types</SelectItem>
            {Object.entries(LISTING_TYPE_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <config.icon className="h-4 w-4" />
                  {config.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {customTaxonomies.map((taxonomy) => {
          const typeConfig = LISTING_TYPE_CONFIG[taxonomy.listing_type] || {};
          const TypeIcon = typeConfig.icon || Tags;
          
          return (
            <Card key={taxonomy.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${typeConfig.color || 'bg-gray-500'}`}>
                      <TypeIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{taxonomy.name}</CardTitle>
                      <Badge variant="outline" className="text-xs mt-1">
                        {typeConfig.label || taxonomy.listing_type}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {taxonomy.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {taxonomy.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {taxonomy.attributes?.length || 0} attributes
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingTaxonomy(taxonomy)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {customTaxonomies.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Layers className="h-8 w-8 mx-auto mb-2" />
          <p>No custom taxonomies found</p>
          <p className="text-sm">Run the seed script to create default taxonomies</p>
        </Card>
      )}

      {/* Edit taxonomy dialog */}
      <Dialog open={!!editingTaxonomy} onOpenChange={() => setEditingTaxonomy(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingTaxonomy && LISTING_TYPE_CONFIG[editingTaxonomy.listing_type] && (
                <div className={`p-1.5 rounded ${LISTING_TYPE_CONFIG[editingTaxonomy.listing_type].color}`}>
                  {React.createElement(LISTING_TYPE_CONFIG[editingTaxonomy.listing_type].icon, { 
                    className: "h-4 w-4 text-white" 
                  })}
                </div>
              )}
              {editingTaxonomy?.name}
            </DialogTitle>
            <DialogDescription>
              Manage attributes for this {editingTaxonomy?.listing_type?.toLowerCase()} taxonomy
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Attributes ({editingTaxonomy?.attributes?.length || 0})</h4>
              <Button size="sm" onClick={() => setShowAttributeDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Attribute
              </Button>
            </div>

            <div className="border rounded-md divide-y max-h-96 overflow-y-auto">
              {editingTaxonomy?.attributes?.map((attr, i) => (
                <div key={attr.id || attr.name || `${attr.title || "attr"}-${i}`} className="p-3 flex items-center justify-between hover:bg-muted/50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{attr.label}</span>
                      {attr.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                      {attr.is_filter && <Badge variant="secondary" className="text-xs">Filter</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>Key: <code className="bg-muted px-1 rounded">{attr.key}</code></span>
                      <Badge variant="outline" className="text-xs">{attr.type}</Badge>
                      {attr.unit && <span>Unit: {attr.unit}</span>}
                    </div>
                    {attr.options?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {attr.options.slice(0, 5).map((opt, j) => (
                          <Badge key={opt.id || opt.name || `${opt.title || "opt"}-${j}`} variant="outline" className="text-xs">{opt}</Badge>
                        ))}
                        {attr.options.length > 5 && (
                          <Badge variant="outline" className="text-xs">+{attr.options.length - 5} more</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDeleteAttribute(attr.key)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {(!editingTaxonomy?.attributes || editingTaxonomy.attributes.length === 0) && (
                <div className="p-8 text-center text-muted-foreground">
                  No attributes defined yet
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add attribute dialog */}
      <Dialog open={showAttributeDialog} onOpenChange={setShowAttributeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Attribute</DialogTitle>
            <DialogDescription>
              Define a new attribute for {editingTaxonomy?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Key (snake_case) *</Label>
                <Input
                  placeholder="e.g., duration_mins"
                  value={newAttribute.key}
                  onChange={(e) => setNewAttribute(prev => ({ 
                    ...prev, 
                    key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Label *</Label>
                <Input
                  placeholder="e.g., Duration (Minutes)"
                  value={newAttribute.label}
                  onChange={(e) => setNewAttribute(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select 
                value={newAttribute.type} 
                onValueChange={(v) => setNewAttribute(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATTRIBUTE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(newAttribute.type === 'select' || newAttribute.type === 'multi_select') && (
              <div className="space-y-2">
                <Label>Options (comma-separated)</Label>
                <Textarea
                  placeholder="Option 1, Option 2, Option 3"
                  value={newAttribute.options?.join(', ') || ''}
                  onChange={(e) => setNewAttribute(prev => ({ 
                    ...prev, 
                    options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  }))}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Placeholder</Label>
                <Input
                  placeholder="Hint text..."
                  value={newAttribute.placeholder}
                  onChange={(e) => setNewAttribute(prev => ({ ...prev, placeholder: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  placeholder="e.g., mins, ₹, kg"
                  value={newAttribute.unit}
                  onChange={(e) => setNewAttribute(prev => ({ ...prev, unit: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Help text for vendors..."
                value={newAttribute.description}
                onChange={(e) => setNewAttribute(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newAttribute.required}
                  onCheckedChange={(v) => setNewAttribute(prev => ({ ...prev, required: v }))}
                />
                <Label>Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newAttribute.is_filter}
                  onCheckedChange={(v) => setNewAttribute(prev => ({ ...prev, is_filter: v }))}
                />
                <Label>Filterable</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttributeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAttribute}>
              Add Attribute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render listing type coverage
  const renderListingTypeCoverage = () => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">Listing Type Coverage</CardTitle>
        <CardDescription>
          Custom taxonomy definitions by listing type
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(LISTING_TYPE_CONFIG).map(([type, config]) => {
            const count = stats?.listing_type_coverage?.[type] || 0;
            return (
              <div 
                key={type}
                className="flex flex-col items-center p-3 rounded-lg border hover:shadow-sm transition-shadow"
              >
                <div className={`p-2 rounded-lg ${config.color} mb-2`}>
                  <config.icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-xs text-muted-foreground">{config.label}</span>
                <span className="text-lg font-bold">{count}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Taxonomy Library</h1>
          <p className="text-muted-foreground">
            Manage product taxonomy and custom attributes for all listing types
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">
              <Database className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="standard">
              <Tags className="h-4 w-4 mr-2" />
              Shopify Taxonomy
            </TabsTrigger>
            <TabsTrigger value="custom">
              <Settings className="h-4 w-4 mr-2" />
              Custom Attributes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {renderStats()}
            {renderSyncStatus()}
            {renderListingTypeCoverage()}
          </TabsContent>

          <TabsContent value="standard" className="mt-6">
            {renderSyncStatus()}
            {renderStandardTaxonomy()}
          </TabsContent>

          <TabsContent value="custom" className="mt-6">
            {renderCustomTaxonomies()}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default TaxonomyLibrary;
